import type { StorefrontBrand, StorefrontCatalog, StorefrontCategoryNode, StorefrontPage as PageDto, StorefrontProductSummary, StorefrontSlide } from '@erp/shared';
import { ChevronLeft, ChevronRight, ShieldCheck, ShoppingBag, Sparkles, Tags, Truck, type LucideIcon } from 'lucide-react';
import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { RTE_CONTENT_CSS } from '../../components/ui/index.js';
import { assetUrl, publicApi } from '../../lib/publicApi.js';
import { sanitizeHtml } from '../../lib/sanitizeHtml.js';

import { pricingRecorder } from './buyerPricing.js';
import { ProductCard } from './ProductCard.js';
import { RecentlyViewed } from './RecentlyViewed.js';
import { HomeSkeleton } from './StorefrontSkeletons.js';
import { StoreImage } from './StoreImage.js';
import { StoriesTray } from './StoriesTray.js';

/** Public storefront home: hero, shop-by-category, featured products, optional content. */
export function StorefrontHomePage(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { slug, site } = useStorefront();
  const [home, setHome] = useState<PageDto | null>(null);
  const [products, setProducts] = useState<StorefrontProductSummary[]>([]);
  const [categories, setCategories] = useState<StorefrontCategoryNode[]>([]);
  const [best, setBest] = useState<StorefrontProductSummary[]>([]);
  const [viewed, setViewed] = useState<StorefrontProductSummary[]>([]);
  /** The discounted subset + how many there are in total (for the "view all"). */
  const [deals, setDeals] = useState<{ items: StorefrontProductSummary[]; total: number }>({ items: [], total: 0 });
  const [brands, setBrands] = useState<StorefrontBrand[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const remember = pricingRecorder(slug);
    void publicApi().get<PageDto | null>(`/${slug}/home`).then((r) => setHome(r.data ?? null)).catch(() => setHome(null));
    // Gate the skeleton on the two visible content blocks (categories + products).
    void Promise.allSettled([
      publicApi().get<StorefrontCatalog>(`/${slug}/products`, { params: { limit: 8 } })
        // Cache the group + currency the server priced this shopper in (guests:
        // the store currency) so the cart/checkout never assume the store's.
        .then((r) => { setProducts(r.data.items); remember(r.data.pricing); }).catch(() => setProducts([])),
      publicApi().get<{ items: StorefrontCategoryNode[] }>(`/${slug}/categories`)
        .then((r) => setCategories(r.data.items)).catch(() => setCategories([])),
    ]).finally(() => setLoading(false));
    // Merchandising rows + brands load independently (they never gate the skeleton).
    void publicApi().get<StorefrontCatalog>(`/${slug}/products`, { params: { sort: 'best_selling', limit: 8 } })
      .then((r) => setBest(r.data.items)).catch(() => setBest([]));
    void publicApi().get<StorefrontCatalog>(`/${slug}/products`, { params: { sort: 'most_viewed', limit: 8 } })
      .then((r) => setViewed(r.data.items)).catch(() => setViewed([]));
    // Deals. `onSale` is the one filter only the server can apply: a discount
    // rule targets a product / brand / category subtree / everything and is
    // resolved against THIS shopper's own price, so there is no client-side
    // predicate for "is it discounted".
    void publicApi().get<StorefrontCatalog>(`/${slug}/products`, { params: { onSale: 'true', limit: 8 } })
      .then((r) => setDeals({
        // Belt and braces: only rows that actually came back with a was-price
        // can show a saving, and a card with no saving in a Deals band is worse
        // than a shorter band.
        items: r.data.items.filter((p) => p.originalPrice != null),
        total: r.data.total,
      }))
      .catch(() => setDeals({ items: [], total: 0 }));
    void publicApi().get<{ items: StorefrontBrand[] }>(`/${slug}/brands`)
      .then((r) => setBrands(r.data.items)).catch(() => setBrands([]));
  }, [slug, i18n.language]);

  const featured = products.filter((p) => p.featured);
  const strip = (featured.length > 0 ? featured : products).slice(0, 4);
  const stripHeading = featured.length > 0 ? t('store.home.featured') : t('store.home.popular');
  const heroImg = assetUrl(site.heroUrl);

  // Image montage for the image-less hero background: categories + brands first
  // (interleaved), then products to fill — absolute URLs, de-duped, capped.
  const heroBgImages = useMemo(() => {
    const abs = (u: string | null | undefined): string | null => assetUrl(u ?? null);
    const cats = categories.map((c) => abs(c.imageUrl)).filter((u): u is string => !!u);
    const brds = brands.map((b) => abs(b.imageUrl)).filter((u): u is string => !!u);
    const prods = [...best, ...products, ...viewed].map((p) => abs(p.imageUrl)).filter((u): u is string => !!u);
    const mix: string[] = [];
    for (let i = 0; i < Math.max(cats.length, brds.length); i++) {
      if (cats[i]) mix.push(cats[i]!);
      if (brds[i]) mix.push(brds[i]!);
    }
    return [...new Set([...mix, ...prods])].slice(0, 24);
  }, [categories, brands, best, products, viewed]);
  // Two slider layouts: rectangle slides ride the full-width banner carousel;
  // square slides render as a multi-up scrollable tile row below it.
  const rectSlides = site.slides.filter((s) => s.shape !== 'square');
  const squareSlides = site.slides.filter((s) => s.shape === 'square');

  if (loading) return <HomeSkeleton />;

  return (
    <div className="flex flex-col gap-12">
      <style>{RTE_CONTENT_CSS}</style>
      {/* القصص — live stories ride above the slider; highlights (أبرز اللحظات) below it */}
      <StoriesTray slug={slug} kind="stories" />
      {rectSlides.length > 0 && <StorefrontSlider slides={rectSlides} bgImages={heroBgImages} />}
      {squareSlides.length > 0 && <StorefrontSquareRow slides={squareSlides} />}
      <StoriesTray slug={slug} kind="highlights" />
      <Helmet>
        <meta property="og:type" content="website" />
        <meta property="og:title" content={home?.title ?? site.storeName} />
        {site.tagline && <meta property="og:description" content={site.tagline} />}
        {assetUrl(site.logoUrl) && <meta property="og:image" content={assetUrl(site.logoUrl)!} />}
      </Helmet>

      {/* Hero — split (text + image) when a hero image is set, else a brand-tinted panel.
          Shown only when the banner slider isn't already acting as the hero (no rect
          slides) so we don't stack two redundant heroes atop each other. */}
      {rectSlides.length === 0 && (
      <section className={`sf-sheen relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-brand/15 via-bg-subtle to-bg-subtle ${heroImg ? 'grid items-stretch lg:grid-cols-2' : ''}`}>
        {!heroImg && <div className="sf-float pointer-events-none absolute -end-12 -top-12 h-48 w-48 rounded-full bg-brand/20 blur-3xl" aria-hidden />}
        <div className="relative flex flex-col justify-center px-6 py-14 sm:px-12 sm:py-20">
          <h1 className="text-3xl font-bold leading-[1.1] tracking-tight sm:text-5xl">{home?.title ?? site.storeName}</h1>
          {site.tagline && <p className="mt-4 max-w-md text-base text-fg-muted sm:text-lg">{site.tagline}</p>}
          <Link
            to={`/s/${slug}/shop`}
            className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-brand-contrast shadow-sm transition-[transform,opacity,box-shadow] duration-150 ease-out hover:-translate-y-0.5 hover:opacity-90 hover:shadow-md active:scale-[0.97] motion-reduce:transform-none"
          >
            <ShoppingBag className="h-4 w-4" aria-hidden />
            {t('store.browse')}
          </Link>
        </div>
        {heroImg && (
          <div className="relative min-h-[15rem] lg:min-h-full">
            <img src={heroImg} alt="" className="absolute inset-0 h-full w-full object-cover" />
          </div>
        )}
      </section>
      )}

      {/* Shop by category — image-filled cards with overlay */}
      {categories.length > 0 && (
        <section className="flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold tracking-tight">{t('store.category.shopBy')}</h2>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {categories.slice(0, 8).map((c, i) => (
              <Link key={c.id} to={`/s/${slug}/category/${c.slug}`} style={{ ['--sf-delay']: `${i * 45}ms` } as CSSProperties} className="sf-card sf-shine group relative aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-bg-subtle transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-1 hover:shadow-lg active:scale-[0.99] motion-reduce:transform-none">
                <StoreImage src={c.imageUrl} alt={c.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-110 motion-reduce:transform-none" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" aria-hidden />
                <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                  <div className="truncate text-sm font-semibold drop-shadow-sm">{c.name}</div>
                  <div className="text-xs opacity-90">{t('store.category.count', { count: c.productCount })}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* عروض — the discounted subset, mirroring the app's Deals rail */}
      <Reveal><DealsBand slug={slug} items={deals.items} total={deals.total} /></Reveal>

      {/* Best sellers — computed from actual POS + online sales */}
      {best.length > 0 && (
        <Reveal>
          <ProductStrip heading={t('store.home.bestSellers', { defaultValue: 'Best sellers' })} products={best.slice(0, 4)} viewAll={`/s/${slug}/shop?sort=best_selling`} viewAllLabel={t('store.home.viewAll')} />
        </Reveal>
      )}

      {/* Shop by brand — most-stocked brands as logo cards */}
      <Reveal><BrandGrid slug={slug} brands={brands} /></Reveal>

      {/* Featured / popular products */}
      {strip.length > 0 && (
        <Reveal>
          <section className="flex flex-col gap-5">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold tracking-tight">{stripHeading}</h2>
              <Link to={`/s/${slug}/shop`} className="text-sm font-medium text-brand hover:underline">{t('store.home.viewAll')}</Link>
            </div>
            <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
              {strip.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </section>
        </Reveal>
      )}

      {/* Most viewed — from the storefront view counter */}
      {viewed.length > 0 && (
        <Reveal>
          <ProductStrip heading={t('store.home.mostViewed', { defaultValue: 'Most viewed' })} products={viewed.slice(0, 4)} viewAll={`/s/${slug}/shop?sort=most_viewed`} viewAllLabel={t('store.home.viewAll')} />
        </Reveal>
      )}

      <RecentlyViewed />

      {home?.bodyHtml && (
        <article className="mx-auto w-full max-w-3xl">
          <div className="rte-content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(home.bodyHtml) }} />
        </article>
      )}
    </div>
  );
}

/** Reveal a section on scroll into view (wires the dormant .sf-reveal class). */
function useReveal<T extends HTMLElement>(): React.RefObject<T> {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') { el.classList.add('is-visible'); return undefined; }
    const ob = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { e.target.classList.add('is-visible'); ob.unobserve(e.target); }
    }, { threshold: 0.1, rootMargin: '0px 0px -60px 0px' });
    ob.observe(el);
    return () => ob.disconnect();
  }, []);
  return ref;
}

function Reveal({ children }: { children: ReactNode }): JSX.Element {
  const ref = useReveal<HTMLDivElement>();
  return <div ref={ref} className="sf-reveal">{children}</div>;
}

/**
 * عروض — the discounted subset of the catalogue, the web half of the app's
 * Deals rail.
 *
 * A tinted plane framing ORDINARY ProductCards rather than new price UI: the
 * card already draws the `-15%` badge and the struck-through was-price, so this
 * band can never disagree with the rest of the store about a number.
 *
 * Renders nothing when there are no deals — the normal state between campaigns,
 * and why there is no skeleton or empty card here.
 */
function DealsBand({ slug, items, total }: {
  slug: string;
  items: StorefrontProductSummary[];
  total: number;
}): JSX.Element | null {
  const { t } = useTranslation('cms');
  if (items.length === 0) return null;
  // The count labels the band, so it may never read LOWER than the band is
  // long — the caller sieves the page it was given, so it can hold fewer rows
  // than the total it came with.
  const count = Math.max(total, items.length);
  const shown = items.slice(0, 4);
  return (
    <section className="relative overflow-hidden rounded-3xl border border-danger/25 bg-bg-subtle p-5 sm:p-6">
      {/* Sale-red bleeding into the brand accent and fading out under the cards.
          Both are theme tokens, so a white-label store re-tints itself. */}
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-danger/15 via-brand/5 to-transparent"
        aria-hidden
      />
      <div className="relative flex flex-col gap-5">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-danger/15 text-danger" aria-hidden>
            <Tags className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold tracking-tight">{t('store.home.deals', { defaultValue: 'Deals' })}</h2>
              {/* A bare numeral — language-neutral, and it sidesteps plurals. */}
              <span className="rounded-full bg-danger/15 px-2 py-0.5 text-xs font-bold tabular-nums text-danger">{count}</span>
            </div>
            <p className="mt-0.5 truncate text-sm text-fg-muted">
              {t('store.home.dealsSubtitle', { defaultValue: 'Limited-time prices' })}
            </p>
          </div>
          {count > shown.length && (
            <Link to={`/s/${slug}/shop?onSale=true`} className="shrink-0 text-sm font-medium text-brand hover:underline">
              {t('store.home.viewAll')}
            </Link>
          )}
        </div>
        <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
          {shown.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </div>
    </section>
  );
}

/** A titled 4-up product row with a "View all" link — shared by the merchandising sections. */
function ProductStrip({ heading, products, viewAll, viewAllLabel }: { heading: string; products: StorefrontProductSummary[]; viewAll: string; viewAllLabel: string }): JSX.Element {
  return (
    <section className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">{heading}</h2>
        <Link to={viewAll} className="text-sm font-medium text-brand hover:underline">{viewAllLabel}</Link>
      </div>
      <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
        {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
      </div>
    </section>
  );
}

/**
 * Shop-by-brand grid: the most-stocked brands first, each a card with the logo on
 * a WHITE plate (so dark/mono brand logos stay visible in dark mode), the brand
 * name and its product count. Hidden entirely when the store has no brands; links
 * to the full brands page when there are more than fit the grid.
 */
function BrandGrid({ slug, brands }: { slug: string; brands: StorefrontBrand[] }): JSX.Element | null {
  const { t } = useTranslation('cms');
  if (!brands || brands.length === 0) return null; // nothing to show → hide the section
  const LIMIT = 12;
  const sorted = [...brands].sort((a, b) => b.productCount - a.productCount);
  const shown = sorted.slice(0, LIMIT);
  return (
    <section className="flex flex-col gap-5">
      <div className="flex items-end justify-between gap-4">
        <h2 className="text-2xl font-bold tracking-tight">{t('store.brands.shopBy', { defaultValue: 'Shop by brand' })}</h2>
        {brands.length > LIMIT && (
          <Link to={`/s/${slug}/brands`} className="shrink-0 text-sm font-medium text-brand hover:underline">
            {t('store.brands.viewAll', { n: brands.length, defaultValue: 'View all ({{n}})' })}
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        {shown.map((b) => (
          <Link
            key={b.id}
            to={`/s/${slug}/brand/${b.id}`}
            className="group flex flex-col items-center gap-2 rounded-2xl border border-border bg-bg-subtle p-3 text-center transition hover:-translate-y-0.5 hover:border-brand/50 hover:shadow-md motion-reduce:transform-none"
          >
            <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-xl bg-white p-3 shadow-sm ring-1 ring-black/5">
              <StoreImage src={b.imageUrl} alt={b.name} className="max-h-full max-w-full object-contain transition duration-300 group-hover:scale-105 motion-reduce:transform-none" logoPad="6%" />
            </div>
            <span className="w-full truncate text-sm font-semibold">{b.name}</span>
            <span className="text-xs text-fg-muted">{t('store.brands.count', { n: b.productCount, defaultValue: '{{n}} products' })}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

/** Only allow http(s) or same-site absolute links as a banner href (defense in depth). */
function safeSlideHref(link: string): string | null {
  const l = link.trim();
  if (/^https?:\/\//i.test(l)) return l;
  if (l.startsWith('/')) return l;
  return null;
}

/** Static feature/trust badges shown on an image-less hero slide. */
const HERO_FEATURES: { key: string; Icon: LucideIcon; labelKey: string; fallback: string }[] = [
  { key: 'delivery', Icon: Truck, labelKey: 'store.features.delivery', fallback: 'Home delivery' },
  { key: 'selection', Icon: Sparkles, labelKey: 'store.features.selection', fallback: 'Wide selection' },
  { key: 'secure', Icon: ShieldCheck, labelKey: 'store.features.secure', fallback: 'Secure checkout' },
];

/** Live montage behind the image-less hero slide: two rows of category/brand/product
    thumbnails drifting in opposite directions. Purely decorative (aria-hidden). */
function HeroMontage({ images }: { images: string[] }): JSX.Element {
  const half = Math.max(1, Math.ceil(images.length / 2));
  const rowA = images.slice(0, half);
  const rowB = images.slice(half);
  // Repeat each row until it comfortably overflows a wide banner, then duplicate the
  // whole strip so the -50% marquee translate loops seamlessly.
  const fill = (arr: string[]): string[] => {
    const base = arr.length ? arr : images;
    const reps = Math.max(2, Math.ceil(12 / base.length));
    const strip = Array.from({ length: reps }, () => base).flat();
    return [...strip, ...strip];
  };
  const tile = (src: string, i: number): JSX.Element => (
    <div
      key={i}
      className="me-2 h-full w-24 shrink-0 overflow-hidden rounded-xl ring-1 ring-white/15 sm:w-32"
      aria-hidden
    >
      <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
    </div>
  );
  // Two half-height rows fill the whole hero (a moving "video wall"); dir=ltr keeps the
  // marquee left-anchored so translateX(-50%) loops seamlessly even in an RTL document.
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" dir="ltr" aria-hidden>
      <div className="absolute inset-x-0 top-0 flex h-1/2 items-center p-1.5">
        <div className="sf-bg-row sf-bg-row-a h-full">{fill(rowA).map(tile)}</div>
      </div>
      <div className="absolute inset-x-0 bottom-0 flex h-1/2 items-center p-1.5">
        <div className="sf-bg-row sf-bg-row-b h-full">{fill(rowB.length >= 2 ? rowB : rowA).map(tile)}</div>
      </div>
    </div>
  );
}

/** Auto-rotating homepage banner slider. Crossfades; dots/arrows to jump; swipe;
    pauses on hover and at 1 slide. */
function StorefrontSlider({ slides, bgImages = [] }: { slides: StorefrontSlide[]; bgImages?: string[] }): JSX.Element {
  const { t } = useTranslation('cms');
  const { slug, site } = useStorefront();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef<number | null>(null);
  const n = slides.length;
  const go = (dir: number): void => setActive((p) => (p + dir + n) % n);
  useEffect(() => {
    if (n <= 1 || paused) return undefined;
    const id = window.setInterval(() => setActive((p) => (p + 1) % n), 5000);
    return () => window.clearInterval(id);
  }, [n, paused]);

  return (
    <section
      className="group relative aspect-[16/7] w-full overflow-hidden rounded-3xl border border-border bg-bg-subtle sm:aspect-[16/5]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => { touchX.current = e.touches[0]?.clientX ?? null; }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
        touchX.current = null;
      }}
    >
      {slides.map((s, idx) => {
        const img = assetUrl(s.imageUrl);
        const href = s.link ? safeSlideHref(s.link) : null;
        const cls = `absolute inset-0 transition-opacity duration-700 ease-out ${idx === active ? 'opacity-100' : 'pointer-events-none opacity-0'}`;

        // No image → a rich, populated brand hero (store name + tagline + feature
        // badges + two CTAs). It owns its own links, so it's NOT wrapped in a single
        // slide-link (that would nest interactive elements).
        if (!img) {
          const secondaryLabel = s.title || t('store.category.shopBy');
          return (
            <div key={s.id} className={cls} aria-hidden={idx !== active}>
              {bgImages.length >= 4 ? (
                <>
                  {/* Solid brand base so the gaps between montage tiles stay on-brand. */}
                  <div className="absolute inset-0 bg-brand" aria-hidden />
                  {/* Live montage of category + brand (+ product) images drifting behind. */}
                  <HeroMontage images={bgImages} />
                  {/* Dark overlay so the montage reads as a subtle background and text stays legible. */}
                  <div className="absolute inset-0 bg-black/45" aria-hidden />
                  {/* Extra center darkening behind the white title/tagline over bright logos. */}
                  <div
                    className="absolute inset-0"
                    style={{ background: 'radial-gradient(ellipse 78% 96% at 50% 50%, rgba(0,0,0,0.55), rgba(0,0,0,0.28) 60%, transparent 100%)' }}
                    aria-hidden
                  />
                  <div className="pointer-events-none absolute -end-20 -top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" aria-hidden />
                </>
              ) : (
                <>
                  <div className="absolute inset-0 bg-gradient-to-br from-brand via-brand to-brand/70" aria-hidden />
                  <div className="pointer-events-none absolute -end-20 -top-24 h-72 w-72 rounded-full bg-white/15 blur-3xl" aria-hidden />
                  <div className="pointer-events-none absolute -start-16 -bottom-20 h-56 w-56 rounded-full bg-black/10 blur-3xl" aria-hidden />
                  <ShoppingBag className="pointer-events-none absolute end-[7%] top-[16%] hidden h-16 w-16 text-white/10 sm:block" aria-hidden />
                  <Sparkles className="pointer-events-none absolute start-[9%] bottom-[18%] hidden h-12 w-12 text-white/10 sm:block" aria-hidden />
                </>
              )}
              <div className="relative flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-brand-contrast sm:gap-4 sm:p-8">
                <h2 className="max-w-2xl text-2xl font-bold leading-tight drop-shadow-lg sm:text-4xl">{site.storeName}</h2>
                {site.tagline && <p className="max-w-xl text-sm text-brand-contrast/90 drop-shadow sm:text-base">{site.tagline}</p>}
                <div className="mt-0.5 flex flex-wrap items-center justify-center gap-2 sm:gap-2.5">
                  {HERO_FEATURES.map((f) => (
                    <span key={f.key} className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium backdrop-blur sm:text-sm">
                      <f.Icon className="h-4 w-4" aria-hidden />{t(f.labelKey, { defaultValue: f.fallback })}
                    </span>
                  ))}
                </div>
                <div className="mt-1 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                  <Link to={`/s/${slug}/shop`} className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-brand shadow-sm transition hover:-translate-y-0.5 hover:shadow-md motion-reduce:transform-none">
                    <ShoppingBag className="h-4 w-4" aria-hidden />{t('store.browse')}
                  </Link>
                  {href && (/^https?:/i.test(href)
                    ? <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white/20 px-5 py-2.5 text-sm font-semibold backdrop-blur transition hover:-translate-y-0.5 hover:bg-white/30 motion-reduce:transform-none">{secondaryLabel}</a>
                    : <Link to={href} className="inline-flex items-center gap-2 rounded-full bg-white/20 px-5 py-2.5 text-sm font-semibold backdrop-blur transition hover:-translate-y-0.5 hover:bg-white/30 motion-reduce:transform-none">{secondaryLabel}</Link>
                  )}
                </div>
              </div>
            </div>
          );
        }

        const body = (
          <>
            <img src={img} alt={s.title ?? ''} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105 motion-reduce:transform-none" />
            {s.title && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent p-6 sm:p-8">
                <h2 className="text-xl font-bold text-white drop-shadow sm:text-3xl">{s.title}</h2>
              </div>
            )}
          </>
        );
        if (href) {
          return /^https?:/i.test(href)
            ? <a key={s.id} href={href} target="_blank" rel="noopener noreferrer" className={cls} aria-hidden={idx !== active}>{body}</a>
            : <Link key={s.id} to={href} className={cls} aria-hidden={idx !== active}>{body}</Link>;
        }
        return <div key={s.id} className={cls} aria-hidden={idx !== active}>{body}</div>;
      })}
      {n > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Previous"
            className="absolute start-3 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-black/35 text-white opacity-0 backdrop-blur transition hover:bg-black/55 focus-visible:opacity-100 group-hover:opacity-100"
          >
            <ChevronLeft className="h-5 w-5 rtl:rotate-180" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Next"
            className="absolute end-3 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-black/35 text-white opacity-0 backdrop-blur transition hover:bg-black/55 focus-visible:opacity-100 group-hover:opacity-100"
          >
            <ChevronRight className="h-5 w-5 rtl:rotate-180" aria-hidden />
          </button>
          <div className="absolute inset-x-0 bottom-3 flex justify-center gap-2">
            {slides.map((s, idx) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(idx)}
                aria-label={`${idx + 1}`}
                className={`h-2 rounded-full transition-all duration-200 ${idx === active ? 'w-6 bg-white' : 'w-2 bg-white/50 hover:bg-white/80'}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

/** Square-slide layout — a horizontally-scrollable row of square tiles (several
    visible at once), the alternative to the full-width banner carousel. Each tile
    is tappable (same safe-href rules as the banner). */
function StorefrontSquareRow({ slides }: { slides: StorefrontSlide[] }): JSX.Element {
  const cls =
    'sf-shine group relative aspect-square w-[46%] shrink-0 snap-start overflow-hidden rounded-2xl border border-border bg-bg-subtle transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-1 hover:shadow-lg active:scale-[0.99] motion-reduce:transform-none sm:w-[31%] md:w-[23%]';
  return (
    <section className="-mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {slides.map((s) => {
        const img = assetUrl(s.imageUrl);
        const href = s.link ? safeSlideHref(s.link) : null;
        const body = img ? (
          <>
            <img src={img} alt={s.title ?? ''} className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105 motion-reduce:transform-none" />
            {s.title && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent p-3">
                <h3 className="truncate text-sm font-semibold text-white drop-shadow">{s.title}</h3>
              </div>
            )}
          </>
        ) : (
          // Image-less square → a filled brand tile with a glow + icon, never hollow.
          <>
            <div className="absolute inset-0 bg-gradient-to-br from-brand via-brand to-brand/70" aria-hidden />
            <div className="pointer-events-none absolute -end-8 -top-10 h-28 w-28 rounded-full bg-white/15 blur-2xl" aria-hidden />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3 text-center text-brand-contrast">
              <ShoppingBag className="h-6 w-6 opacity-90" aria-hidden />
              {s.title && <h3 className="text-sm font-semibold leading-snug drop-shadow">{s.title}</h3>}
            </div>
          </>
        );
        if (href) {
          return /^https?:/i.test(href)
            ? <a key={s.id} href={href} target="_blank" rel="noopener noreferrer" className={cls}>{body}</a>
            : <Link key={s.id} to={href} className={cls}>{body}</Link>;
        }
        return <div key={s.id} className={cls}>{body}</div>;
      })}
    </section>
  );
}
